import * as React from 'react';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import { ChevronDown, ChevronUp } from 'lucide-react';


const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root>
>(({ className, children, ...props }, ref) => {
  const viewportRef = React.useRef<HTMLDivElement | null>(null);

  const [canScrollUp, setCanScrollUp] = React.useState<boolean>(false);
  const [canScrollDown, setCanScrollDown] = React.useState<boolean>(false);

  const updateScrollButtonState = React.useCallback(() => {
    const viewportEl = viewportRef.current;
    if (viewportEl === null) {
      setCanScrollUp(false);
      setCanScrollDown(false);
      return;
    }
    const isScrollable = viewportEl.scrollHeight > viewportEl.clientHeight;
    const up = viewportEl.scrollTop > 0;
    const down = viewportEl.scrollTop + viewportEl.clientHeight < viewportEl.scrollHeight;
    setCanScrollUp(isScrollable && up);
    setCanScrollDown(isScrollable && down);
  }, []);

  React.useEffect(() => {
    const viewportEl = viewportRef.current;
    if (viewportEl === null) {
      return;
    }
    updateScrollButtonState();
    const onScroll = () => updateScrollButtonState();
    viewportEl.addEventListener('scroll', onScroll, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateScrollButtonState) : undefined;
    if (ro !== undefined) {
      ro.observe(viewportEl);
    }
    return () => {
      viewportEl.removeEventListener('scroll', onScroll);
      if (ro !== undefined) {
        ro.disconnect();
      }
    };
  }, [updateScrollButtonState]);

  const scrollByAmount = (deltaY: number): void => {
    const viewportEl = viewportRef.current;
    if (viewportEl === null) {
      return;
    }
    viewportEl.scrollBy({ top: deltaY, behavior: 'smooth' });
  };

  return (
    <ScrollAreaPrimitive.Root
      ref={ref}
      className='relative overflow-hidden'
      type="always"
      {...props}
    >
      <ScrollAreaPrimitive.Viewport ref={viewportRef} className="h-full w-full rounded-[inherit]">
        {children}
      </ScrollAreaPrimitive.Viewport>

      {/* Always-visible vertical scroll buttons on desktop */}
      <button
        type="button"
        aria-label="Scroll up"
        className={`eoe-scroll-up-btn hidden md:flex items-center justify-center absolute right-1 top-1 z-10 size-7 rounded-full border-0 bg-eoe-bg dark:bg-eoe-dark-bg text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke hover:bg-eoe-icon dark:hover:bg-eoe-dark-icon-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed`}
        onClick={() => scrollByAmount(-80)}
        disabled={!canScrollUp}
      >
        <ChevronUp className="w-4 h-4" />
      </button>

      <button
        type="button"
        aria-label="Scroll down"
        className={`eoe-scroll-down-btn hidden md:flex items-center justify-center absolute right-1 bottom-1 z-10 size-7 rounded-full border-0 bg-eoe-bg dark:bg-eoe-dark-bg text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke hover:bg-eoe-icon dark:hover:bg-eoe-dark-icon-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed`}
        onClick={() => scrollByAmount(80)}
        disabled={!canScrollDown}
      >
        <ChevronDown className="w-4 h-4" />
      </button>

      <ScrollBar />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
});
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = 'vertical', ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={`
      flex touch-none select-none transition-colors ${orientation === 'vertical' && 'h-full w-2.5 border-l border-l-transparent p-[1px]'}
      ${orientation === 'horizontal' && 'h-2.5 flex-col border-t border-t-transparent p-[1px]'}
    `}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };